import React, { useState } from "react";
import {
  Button,
  Form,
  Table,
  Popconfirm,
  Card,
  Tag,
  Tooltip,
  Tabs,
  Select,
  Badge,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useExcuses } from "../../../hooks/useExcuse";
import { useEmployees } from "../../../hooks/useEmployee";
import ExcuseFormModal from "./ExcuseFormModal";
import RespondRequestModal from "./RespondRequestModal";

const { Option } = Select;

const ExcuseManagement = () => {
  const [form] = Form.useForm();
  const {
    useGetExcuses,
    useCreateExcuse,
    useEditExcuse,
    useDeleteExcuse,
    useGetExcuseRequests,
    useRespondExcuseRequest,
  } = useExcuses();

  const { useGetEmployees } = useEmployees();

  const { data: excuses = [], isLoading } = useGetExcuses();
  const { data: employees = [] } = useGetEmployees(null, null);
  const createMutation = useCreateExcuse();
  const editMutation = useEditExcuse();
  const deleteMutation = useDeleteExcuse();

  const [activeTab, setActiveTab] = useState("1");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingExcuse, setEditingExcuse] = useState(null);
  const [searchText] = useState("");

  const [requestFilters, setRequestFilters] = useState({});
  const { data: excuseRequests = [], isLoading: loadingRequests } =
    useGetExcuseRequests(requestFilters);
  const respondMutation = useRespondExcuseRequest();
  const [isRespondModalVisible, setIsRespondModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const handleAdd = () => {
    setEditingExcuse(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (excuse) => {
    setEditingExcuse(excuse);
    form.setFieldsValue({
      name: excuse.name,
      description: excuse.description,
    });
    setIsModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingExcuse) {
        await editMutation.mutateAsync({ id: editingExcuse.id, ...values });
      } else {
        await createMutation.mutateAsync(values);
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch (err) {
      console.error("Validation failed:", err);
    }
  };

  const handleDelete = async (id) => {
    await deleteMutation.mutateAsync(id);
  };

  const handleRespond = (request) => {
    setSelectedRequest(request);
    setIsRespondModalVisible(true);
  };

  const handleRespondSubmit = async (responseData) => {
    await respondMutation.mutateAsync(responseData);
    setIsRespondModalVisible(false);
    setSelectedRequest(null);
  };

  // Filter excuses based on search text
  const filteredExcuses = excuses.filter(
    (excuse) =>
      excuse.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (excuse.description &&
        excuse.description.toLowerCase().includes(searchText.toLowerCase()))
  );

  const getStatusTag = (status) => {
    const statusMap = {
      0: { color: "warning", icon: <ClockCircleOutlined />, text: "Pending" },
      1: { color: "success", icon: <CheckCircleOutlined />, text: "Approved" },
      2: { color: "error", icon: <CloseCircleOutlined />, text: "Rejected" },
    };
    const config = statusMap[status] || statusMap[0];
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const excuseColumns = [
    {
      title: "#",
      width: 60,
      render: (_, __, index) => (
        <span className="text-gray-600 font-medium">{index + 1}</span>
      ),
    },
    {
      title: "Excuse Title",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (text) => (
        <div className="flex items-center gap-2">
          <FileTextOutlined className="text-primary" />
          <span className="font-semibold text-primary">{text}</span>
        </div>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 280,
      render: (text) => (
        <Tooltip title={text}>
          <span className="text-gray-600">
            {text ? (
              text.length > 100 ? `${text.substring(0, 100)}...` : text
            ) : (
              <Tag color="default">No description</Tag>
            )}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      width: 110,
      fixed: "right",
      render: (_, record) => (
        <div className="flex gap-2">
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              type="text"
              className="text-primary hover:text-primary_hover"
            />
          </Tooltip>
          <Popconfirm
            title="Delete Excuse?"
            description="Are you sure you want to delete this excuse?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button
                danger
                icon={<DeleteOutlined />}
                type="text"
                loading={
                  deleteMutation.isPending &&
                  deleteMutation.variables === record.id
                }
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const requestColumns = [
    {
      title: "Employee",
      dataIndex: "employeeName",
      key: "employeeName",
      width: 160,
      render: (text) => (
        <span className="font-semibold text-primary">{text}</span>
      ),
    },
    {
      title: "Excuse Type",
      dataIndex: "excuseName",
      key: "excuseName",
      width: 160,
      render: (text) => (
        <div className="flex items-center gap-2">
          <FileTextOutlined className="text-gray-500" />
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: "Period",
      key: "period",
      width: 130,
      render: (_, record) => (
        <div className="text-sm">
          <div>{new Date(record.startDate).toLocaleDateString()}</div>
          <div className="text-gray-500">to</div>
          <div>{new Date(record.endDate).toLocaleDateString()}</div>
        </div>
      ),
    },
    {
      title: "Days",
      dataIndex: "daysRequested",
      key: "daysRequested",
      width: 80,
      render: (days) => (
        <Badge count={days} showZero style={{ backgroundColor: "#298E9F" }} />
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: 200,
      render: (text) => (
        <Tooltip title={text}>
          <span className="text-gray-600">
            {text ? (
              text.length > 50 ? `${text.substring(0, 50)}...` : text
            ) : (
              <Tag color="default">No reason</Tag>
            )}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: "Actions",
      width: 110,
      fixed: "right",
      render: (_, record) => (
        <div className="flex gap-2">
          {record.status === 0 && (
            <Tooltip title="Respond">
              <Button
                type="primary"
                size="small"
                onClick={() => handleRespond(record)}
                className="bg-primary hover:bg-primary_hover"
              >
                Respond
              </Button>
            </Tooltip>
          )}
          {record.status !== 0 && <Tag color="default">Responded</Tag>}
        </div>
      ),
    },
  ];

  const pendingCount = excuseRequests.filter((req) => req.status === 0).length;

  const tabItems = [
    {
      key: "1",
      label: "Excuse Types",
      children: (
        <div>
          <div className="mb-4 flex sm:justify-end">
            <Button
              icon={<PlusOutlined />}
              onClick={handleAdd}
              size="large"
              type=""
              className="w-full sm:w-auto bg-gradient-to-r text-white from-primary to-primary_hover hover:bg-primary_hover hover:scale-105 hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all"
            >
              Add Excuse
            </Button>
          </div>
          <Table
            columns={excuseColumns}
            dataSource={filteredExcuses}
            loading={isLoading}
            rowKey="id"
            bordered
            size="middle"
            scroll={{ x: 650 }}
            rowClassName={(record, index) => (index % 2 === 0 ? "bg-gray-50" : "")}
            pagination={{
              pageSize: 10,
              size: "small",
              showTotal: (total) => `Total ${total} excuses`,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
            }}
          />
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <span>
          Excuse Requests
          {pendingCount > 0 && <Badge count={pendingCount} offset={[10, 0]} />}
        </span>
      ),
      children: (
        <div>
          <div className="mb-4 flex flex-col sm:flex-row gap-2 sm:flex-wrap">
            <Select
              placeholder="Filter by Status"
              allowClear
              className="w-full sm:w-[200px]"
              onChange={(value) =>
                setRequestFilters({ ...requestFilters, Status: value })
              }
              value={requestFilters.Status}
            >
              <Option value={0}>
                <span className="flex items-center gap-2">
                  <ClockCircleOutlined className="text-orange-500" />
                  Pending
                </span>
              </Option>
              <Option value={1}>
                <span className="flex items-center gap-2">
                  <CheckCircleOutlined className="text-green-500" />
                  Approved
                </span>
              </Option>
              <Option value={2}>
                <span className="flex items-center gap-2">
                  <CloseCircleOutlined className="text-red-500" />
                  Rejected
                </span>
              </Option>
            </Select>

            <Select
              placeholder="Filter by Employee"
              allowClear
              className="w-full sm:w-[250px]"
              onChange={(value) =>
                setRequestFilters({ ...requestFilters, EmployeeId: value })
              }
              value={requestFilters.EmployeeId}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
              }
            >
              {employees.map((employee) => (
                <Option key={employee.id} value={employee.id}>
                  {employee.fullName}
                </Option>
              ))}
            </Select>

            <Select
              placeholder="Filter by Excuse Type"
              allowClear
              className="w-full sm:w-[250px]"
              onChange={(value) =>
                setRequestFilters({ ...requestFilters, ExcuseId: value })
              }
              value={requestFilters.ExcuseId}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
              }
            >
              {excuses.map((excuse) => (
                <Option key={excuse.id} value={excuse.id}>
                  <span className="flex items-center gap-2">
                    <FileTextOutlined />
                    {excuse.name}
                  </span>
                </Option>
              ))}
            </Select>

            {(requestFilters.Status !== undefined ||
              requestFilters.EmployeeId ||
              requestFilters.ExcuseId) && (
              <Button
                onClick={() => setRequestFilters({})}
                type="link"
                danger
                className="self-start sm:self-auto"
              >
                Clear Filters
              </Button>
            )}
          </div>
          <Table
            columns={requestColumns}
            dataSource={excuseRequests}
            loading={loadingRequests}
            rowKey="id"
            bordered
            size="middle"
            scroll={{ x: 950 }}
            rowClassName={(record, index) => (index % 2 === 0 ? "bg-gray-50" : "")}
            pagination={{
              pageSize: 10,
              size: "small",
              showTotal: (total) => `Total ${total} requests`,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="p-2 sm:p-6 bg-[#F7FCFD]">
      <Card className="shadow-sm [&_.ant-card-body]:!p-3 sm:[&_.ant-card-body]:!p-6">
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>

      <ExcuseFormModal
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        onSubmit={handleSubmit}
        form={form}
        editingExcuse={editingExcuse}
      />

      <RespondRequestModal
        visible={isRespondModalVisible}
        onCancel={() => {
          setIsRespondModalVisible(false);
          setSelectedRequest(null);
        }}
        onSubmit={handleRespondSubmit}
        request={selectedRequest}
        loading={respondMutation.isPending}
      />
    </div>
  );
};

export default ExcuseManagement;